import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { buildOrdatMutationVariables } from './mutationVariables.ts';
import OrdatIntegrationForm from './OrdatIntegrationForm.tsx';
import { CREATE_ORDAT_INTEGRATION, GET_ORDAT_INTEGRATIONS } from './queries.ts';
import type { OrdatFormData } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ORDAT_PATH } from '../../constants.ts';

const FORM_ID = 'ordat-integration-form';

const OrdatIntegrationAddPage = () => {
    const { t } = useTranslation('integrations');
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('ordat.title')}`;

    const [createIntegration, { loading: isSubmitting }] = useMutation(CREATE_ORDAT_INTEGRATION, {
        refetchQueries: [GET_ORDAT_INTEGRATIONS],
    });

    const handleCreateIntegration = async (formData: OrdatFormData) => {
        try {
            await createIntegration({ variables: buildOrdatMutationVariables(formData) });
            publishToast({ description: t('ordat.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_ORDAT_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('ordat.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('ordat.title'), to: url(CUSTOMER_INTEGRATION_ORDAT_PATH) },
                            { label: t('ordat.form.createTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDAT_PATH)}>{t('ordat.form.cancelButton')}</Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID} loading={isSubmitting}>
                            {t('ordat.form.createButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <OrdatIntegrationForm onSubmit={handleCreateIntegration} formId={FORM_ID} />
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrdatIntegrationAddPage;
